import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { useChat } from '../../../hooks/useChat';
import { useVoiceRecording } from '../../../hooks/useVoiceRecording';
import { ChatInput } from './ChatInput';
import { ChatMessageDetailPanel } from './ChatMessageDetailPanel';
import { ChatMosaicView } from './ChatMosaicView';
import { VoiceRecorder } from '../../shared';
import { Loader } from '../../../components/ui';
import type { Topic } from '../../../types/topic';
import type { Project } from '../../../types/project';
import { cn } from '../../../lib/utils';
import { useStatusBarInfo } from '../../../hooks/useStatusBarInfo';
import { useViewport } from '../../../contexts/ViewportContext';
import { useDeleteChatMessage, useRetryAIResponse } from '../../../hooks/queries/useChats';
import { useTopicSubscription } from '../../../hooks/useTopicSubscription';
import { chatKeys } from '../../../hooks/queries/chatKeys';
import { FormattedChatMessage } from '../../../hooks/queries/chatTypes';
import { useInitialLoadingScreenManager } from '../../../hooks/useInitialLoadingScreenManager';

interface ChatTopicProps {
  topic: Topic;
  project?: Project;
  hasCollaborators?: boolean;
  onBackClick: () => void;
}

export function ChatTopic({ topic, onBackClick }: ChatTopicProps) {
  // UI state
  const [input, setInput] = useState('');
  const [detailMessage, setDetailMessage] = useState<FormattedChatMessage | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showFooterShadow, setShowFooterShadow] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useViewport();
  const { isNativeApp, statusBarHeight, navigationBarHeight } = useStatusBarInfo();

  // React Query hooks for chat functionality
  const { isTyping, isSilentlyRetrying, retryAttempt, generateAIResponse, messages, isLoading } =
    useChat(topic.id);

  // Mutations for chat operations
  const deleteMessage = useDeleteChatMessage();
  const retryAIResponse = useRetryAIResponse();

  // Set up real-time subscriptions for chat messages
  // Memoize subscriptions to prevent unnecessary re-subscriptions
  const subscriptions = useMemo(
    () => [
      {
        table: 'ai_chat_messages' as const,
        queryKey: chatKeys.byTopic(topic.id),
      },
    ],
    [topic.id]
  );

  useTopicSubscription(topic.id, subscriptions);

  // Note: Tracking functions removed - using invalidation-based approach instead

  // Voice recording hook
  const {
    isRecording,
    recordingTime,
    error: recordingError,
    startRecording,
    stopRecording,
    cancelRecording,
  } = useVoiceRecording();

  // Handle loading screen state with custom hook
  const { markRouteComplete } = useInitialLoadingScreenManager();

  // Only mark as complete when loading is done
  // Using a ref to track if we've already marked complete to avoid infinite updates
  const hasMarkedCompleteRef = useRef(false);
  useEffect(() => {
    if (!isLoading && !hasMarkedCompleteRef.current) {
      hasMarkedCompleteRef.current = true;
      markRouteComplete();
    }
  }, [isLoading]); // Remove markRouteComplete from deps to prevent infinite updates

  // Update footer shadow when messages change
  useEffect(() => {
    // Small delay to ensure DOM is updated
    setTimeout(() => {
      if (contentRef.current) {
        const container = contentRef.current;
        const hasOverflow = container.scrollHeight > container.clientHeight;
        const scrollTop = container.scrollTop;
        const isAtBottom =
          Math.abs(container.scrollHeight - container.clientHeight - scrollTop) < 10;

        setShowFooterShadow(hasOverflow && !isAtBottom);
      }
    }, 100);
  }, [messages.length]);

  // Watch for deletions of the currently displayed detail message
  useEffect(() => {
    if (detailMessage && !messages.some(msg => msg.id === detailMessage.id)) {
      // The message being viewed in detail panel was deleted, close the panel
      setDetailMessage(null);
    }
  }, [messages, detailMessage]);

  // Add scroll listener to detect when to show header and footer shadows
  useEffect(() => {
    const handleScroll = () => {
      if (contentRef.current) {
        const container = contentRef.current;
        const scrollTop = container.scrollTop;

        // Show header shadow when scrolled down
        setIsScrolled(scrollTop > 10);

        // Show footer shadow only when:
        // 1. Content height exceeds the visible area AND
        // 2. User hasn't scrolled to the bottom
        const hasOverflow = container.scrollHeight > container.clientHeight;
        const isAtBottom =
          Math.abs(container.scrollHeight - container.clientHeight - scrollTop) < 10;

        setShowFooterShadow(hasOverflow && !isAtBottom);
      }
    };

    const scrollContainer = contentRef.current;
    if (scrollContainer) {
      // Run the check after a small delay to ensure content is rendered
      setTimeout(handleScroll, 100);

      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Form submission handler
  const handleSubmit = async (e: React.FormEvent, model?: string) => {
    e.preventDefault();
    if (!input.trim()) return;

    try {
      const userMessage = input.trim();
      setInput('');
      await generateAIResponse(userMessage, model);
    } catch (error) {
      console.error('Error in message submission:', error);
    }
  };

  // Voice recording handler
  const handleVoiceMessage = async () => {
    if (isRecording) {
      try {
        const result = await stopRecording();
        const text = result?.transcription;

        if (text && text.trim()) {
          const savedModel = localStorage.getItem('zentime_selected_ai_model');
          await generateAIResponse(text.trim(), savedModel || undefined);
        }
      } catch (error) {
        console.error('Error processing voice message:', error);
      }
    } else {
      try {
        await startRecording();
      } catch (error) {
        console.error('Error starting recording:', error);
      }
    }
  };

  // Message deletion handler
  const handleDelete = async (messageId: string) => {
    try {
      // Check if we're deleting the message currently displayed in the detail panel
      const isDetailedMessageBeingDeleted = detailMessage?.id === messageId;

      // Note: No longer tracking local deletes - using invalidation approach

      // Call the React Query mutation to delete the message
      await deleteMessage.mutateAsync({
        messageId,
        topicId: topic.id,
        deletePaired: true, // Delete paired messages
      });

      // Close the detail panel if the deleted message is currently being displayed
      if (isDetailedMessageBeingDeleted) {
        setDetailMessage(null);
      }
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  // Handle retry of AI response after an error
  const handleRetry = async (messageId: string) => {
    try {
      // Get saved model preference if any
      const savedModel = localStorage.getItem('zentime_selected_ai_model') || undefined;

      // Use the retry mutation
      const result = await retryAIResponse.mutateAsync({
        topicId: topic.id,
        errorMessageId: messageId,
        model: savedModel,
      });

      // If successful and online, generate a new response
      if (result.success) {
        // Get the most recent user message
        const userMessages = messages.filter(m => m.role === 'user');
        const lastUserMessage = userMessages[userMessages.length - 1];

        if (lastUserMessage) {
          await generateAIResponse(lastUserMessage.content, savedModel);
        }
      }
    } catch (error) {
      console.error('Error retrying AI response:', error);
    }
  };

  // Close detail panel
  const handleCloseDetail = () => {
    setDetailMessage(null);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Mobile header with shadow on scroll */}
      <div
        className={`lg:hidden flex items-center h-14 px-4 z-10 sticky top-0 transition-shadow duration-200 ${
          isScrolled ? 'shadow-md' : ''
        }`}
        style={{
          paddingTop: isMobile && isNativeApp ? `${statusBarHeight}px` : '0',
          height: isMobile && isNativeApp ? `calc(3.5rem + ${statusBarHeight}px)` : '3.5rem',
        }}
      >
        <div className="flex-none">
          <button
            onClick={onBackClick}
            className="p-2 -ml-2 text-gray-600 hover:text-gray-900 back-button"
          >
            <ChevronLeft size={24} />
          </button>
        </div>

        <div className="absolute left-1/2 -translate-x-1/2">
          <span className="text-base font-semibold text-gray-900 whitespace-nowrap">
            {topic.name}
          </span>
        </div>

        <div className="flex-none w-10" />
      </div>

      {/* Retry indicator - fixed at the bottom above the input to avoid moving content */}
      {isSilentlyRetrying && (
        <div
          className="fixed left-0 right-0 bg-yellow-50 text-yellow-700 text-xs px-3 py-2 text-center border-t border-yellow-100 z-10 shadow-sm"
          style={{
            bottom: isMobile && isNativeApp ? `calc(72px + ${navigationBarHeight}px)` : '72px',
          }}
        >
          <div className="max-w-3xl mx-auto flex items-center justify-center gap-1">
            <span className="inline-block w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></span>
            Connection issue detected, retrying automatically (attempt {retryAttempt} of 2)...
          </div>
        </div>
      )}

      {/* Messages */}
      <div ref={contentRef} className="flex-1 overflow-y-auto relative">
        {isLoading ? (
          // Notify loading screen that topic is not fully loaded yet
          <div className="h-full w-full flex flex-col items-center justify-center">
            <Loader className="mb-4" />
          </div>
        ) : null}
        <ChatMosaicView
          messages={messages}
          isLoading={isLoading}
          isTyping={isTyping}
          selectedMessageId={detailMessage?.id}
          onMessageSelect={message => {
            // Check if this is a delete request
            if (message.id.startsWith('delete:')) {
              const messageId = message.id.replace('delete:', '');
              handleDelete(messageId);
              return;
            }

            // Check if this is a retry request
            if (message.id.startsWith('retry:')) {
              const actualMessageId = message.id.replace('retry:', '');
              handleRetry(actualMessageId);
              return;
            }

            setDetailMessage(message);
          }}
        />

        {/* Background overlay when detail panel is open */}
        <div
          className={cn(
            'fixed inset-0 bg-black transition-opacity duration-500',
            detailMessage
              ? 'bg-opacity-25 z-40 opacity-100'
              : 'bg-opacity-0 z-0 opacity-0 pointer-events-none'
          )}
          onClick={handleCloseDetail} // Close panel when clicking overlay
        />

        {/* Detail panel */}
        <ChatMessageDetailPanel
          message={
            detailMessage || {
              id: '',
              content: '',
              role: 'assistant',
              createdAt: new Date().toISOString(),
              highlights: [],
              summary: '',
            }
          }
          onClose={handleCloseDetail}
          onDelete={handleDelete}
          visible={!!detailMessage}
        />
      </div>

      {/* Input */}
      <div
        className={`w-full sticky bottom-0 z-10 transition-shadow duration-200 ${
          showFooterShadow
            ? 'lg:shadow-none md:shadow-none shadow-[0_-2px_4px_rgba(0,0,0,0.1)]'
            : ''
        }`}
        style={{
          paddingBottom: isMobile && isNativeApp ? `${navigationBarHeight}px` : '0',
        }}
      >
        {recordingError && (
          <div className="px-3 pt-3">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-600 text-sm">
              {recordingError}
            </div>
          </div>
        )}

        <VoiceRecorder
          isRecording={isRecording}
          recordingTime={recordingTime}
          onCancel={cancelRecording}
        />

        <div className="p-3">
          <ChatInput
            input={input}
            isRecording={isRecording}
            onInputChange={setInput}
            onSubmit={handleSubmit}
            onVoiceClick={handleVoiceMessage}
            isTyping={isTyping}
            botName="Zen Assistant"
          />
        </div>
      </div>
    </div>
  );
}
